import {
  type GenericObject,
  type PublishError,
} from '@bizzdesign/modeler/models';
import { type HashMap, type TranslateParams } from '@jsverse/transloco';
import {
  type PayloadInvalidError,
  type Specialization,
} from '@bizzdesign/sdk-knowledgegraph';

/**
 * Generates translations for the first x errors from the error array parameter
 * @param errors - Error object from SDK that contains an array of errors.
 * @param activeLang - Current active language.
 * @param draftObject - Current draft model from where we will find name of the elements.
 * @param translate - Function responsible for translations.
 * @param length - The number of how many errors we would like to return. If not specified, default is 5.
 */
export function generatePublishViewErrorsTranslations(
  errors: PublishError[],
  activeLang: string,
  draftObject: GenericObject,
  translate: (key: TranslateParams, params?: HashMap, lang?: string) => string,
  length: number = 5,
): string[] {
  return Array.from({ length })
    .filter((_, index) => errors?.[index])
    .map((_, index) => {
      const err = errors[index];
      let currentError: PublishError;
      let elementNames: string;
      switch (err.type) {
        case 'ELEMENTS_NAME_NOT_UNIQUE':
          currentError = err;
          elementNames = generateNamesStringForIds(
            currentError.elements.map((e) => e.id),
            activeLang,
            draftObject,
          );
          return handleElementsNameNotUniqueError(
            currentError.specialization,
            elementNames,
            translate,
          );

        case 'INVALID_INPUT':
          currentError = err;
          elementNames = generateNamesStringForIds(
            currentError.ids,
            activeLang,
            draftObject,
          );
          return translate(
            'publishingErrors.InvalidInput',
            { elementNames },
            'featureModeler',
          );

        case 'EMPTY_VIEW':
          return translate('publishingErrors.EmptyView', {}, 'featureModeler');

        case 'RELATION_SOURCE_NOT_FOUND':
          currentError = err;
          elementNames = generateNamesStringForIds(
            currentError.relationIds,
            activeLang,
            draftObject,
          );
          return translate(
            'publishingErrors.RelationSourceNotFound',
            { elementNames },
            'featureModeler',
          );

        case 'RELATION_TARGET_NOT_FOUND':
          currentError = err;
          elementNames = generateNamesStringForIds(
            currentError.relationIds,
            activeLang,
            draftObject,
          );
          return translate(
            'publishingErrors.RelationTargetNotFound',
            { elementNames },
            'featureModeler',
          );

        case 'REFERENCED_ELEMENTS_NOT_FOUND':
          currentError = err;
          elementNames = generateNamesStringForIds(
            currentError.referencedElementIds,
            activeLang,
            draftObject,
          );
          return translate(
            'publishingErrors.ReferencedElementNotFound',
            { elementNames },
            'featureModeler',
          );

        case 'PAYLOAD_INVALID':
          currentError = err;
          elementNames = generateNamesStringForIds(
            currentError.ids,
            activeLang,
            draftObject,
          );
          return handlePayloadInvalidError(
            currentError.location,
            elementNames,
            translate,
          );

        default:
          return translate('publishingErrors.Unknown', {}, 'featureModeler');
      }
    });
}

function handlePayloadInvalidError(
  location: PayloadInvalidError['location'],
  elementNames: string,
  translate: (key: TranslateParams, params?: HashMap, lang?: string) => string,
): string {
  if (location === 'REFERENCED_ELEMENTS') {
    return translate(
      'publishingErrors.InvalidPayload.ReferencedElements',
      { elementNames },
      'featureModeler',
    );
  } else if (location === 'ATTRIBUTE_CATEGORIES') {
    return translate(
      'publishingErrors.InvalidPayload.AttributeCategories',
      { elementNames },
      'featureModeler',
    );
  }

  return translate(
    'publishingErrors.InvalidPayload',
    { elementNames },
    'featureModeler',
  );
}

function generateNamesStringForIds(
  ids: string[],
  activeLang: string,
  draftObject: GenericObject,
): string {
  return ids
    .map(
      (id) =>
        draftObject[id]?.name[activeLang] || draftObject[id]?.typeName || id,
    )
    .join(', ');
}

function handleElementsNameNotUniqueError(
  specialization: Specialization,
  elementNames: string,
  translate: (key: TranslateParams, params?: HashMap, lang?: string) => string,
): string {
  if (specialization === 'view') {
    return translate(
      'publishingErrors.ViewNameNotUnique',
      { elementNames },
      'featureModeler',
    );
  } else if (specialization === 'object') {
    return translate(
      'publishingErrors.ObjectNameNotUnique',
      { elementNames },
      'featureModeler',
    );
  } else if (specialization === 'relation') {
    return translate(
      'publishingErrors.RelationNameTypeCombinationNotUnique',
      { elementNames },
      'featureModeler',
    );
  }

  return translate(
    'publishingErrors.ElementsNameNotUnique',
    { elementNames },
    'featureModeler',
  );
}
